import {
  InferConfigType,
  InferResponseType,
  MakeAllKeysRequired,
} from '../types';
import {DataFetcherRequest} from '../useRequest';
import {useLegacyGrpcClientConfig} from './GrpcClientContext';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';
import {
  GrpcQueryConfig,
  GrpcQueryConfigWithSection,
  GrpcRequestServiceMethod,
  useGenericGrpcRequest,
} from './useGenericGrpcRequest';

// not importing the original Device enum here due to bundle size regression
// import {Device} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
export enum GripsDevice {
  MOBILE_WEB = 3,
  DESKTOP = 4,
}

export type GenericGripsConfig = {
  // intentional empty so that the configs can be passed inline for legacy Grips requests
  // In Grips there is no definite shape for configs, so we can't use a single type here
  device?: GripsDevice;
  shopId?: number;
  childShopId?: number;
  deprecatedParentShopId?: number;
};

/**
 * This should be used only for legacy grips service methods which are not yet migrated to Tadarida backend.
 * This will be removed in the future.
 * A convenience wrapper around `useRequest` to do GRPC requests.
 * The GRPC `channel` will be read from context.
 * @param ServiceMethod the GRPC service method to call
 * @param params the parameters for the specific GRPC function
 */
export function useLegacyGrpcRequest<
  ServiceMethod extends GrpcRequestServiceMethod,
  MethodName extends Awaited<ServiceMethod['method']>,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  >,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<Awaited<ReturnType<ServiceMethod>>>[1],
      keyof GenericGripsConfig
    >
  >,
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
>(
  lazyAsyncService: ServiceMethod,
  params: ParamsType,
  grpcQueryConfig: GrpcQueryConfig<ResponseType, ParamsType> = {},
): DataFetcherRequest<ResponseType> {
  const {shopId, childShopId, device} = useLegacyGrpcClientConfig();
  const genericConfig = {
    shopId,
    childShopId,
    device: device === 'mobile' ? GripsDevice.MOBILE_WEB : GripsDevice.DESKTOP,
  };

  const query = useGenericGrpcRequest(
    lazyAsyncService,
    convertAllNullPropertiesToUndefined<ParamsType>(params) as any,
    genericConfig,
    grpcQueryConfig as GrpcConfigType,
  );

  return query as DataFetcherRequest<ResponseType>;
}
